import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { getImageUrl } from '@apps/main/modules/api/hotels';
import { isHotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type {
  RecipeMainIngredient,
  HotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { CloseHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import {
  useEditingIngredientStore,
  useItineraryStore,
  usePaxStore,
  useRouterStore,
  useStatsigEventsStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useGlobalCurrencyStore } from '@apps/main/new-desktop/core/stores/globalCurrencyStore';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import { usePreviouslyUserSessionStore } from '@apps/main/new-desktop/core/stores/previouslyUserSession/previouslyUserSessionStore';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import { trackNightsChanged } from '@libs/google-analytics-events';
import type { IngredientId } from '@libs/trip-planner-interfaces';
import {
  createIngredientId,
  IngredientType,
} from '@libs/trip-planner-interfaces';

import type {
  HotelSelectionCardData,
  HotelSelectionDestinationData,
  HotelSelectionEventTypes,
} from '../../models';
import { HotelSelectionEventType, HotelSelectionViewType } from '../../models';
import { useHotelSelectionModalsStore } from '../hotelSelectionModalsStore';

import { useHotelSelectionListStore } from './hotelSelectionListStore';
import {
  getHotelSelectionHeaderData,
  mapAvailabilityOrderToSuggestionType,
  mapDestinationProductToHotelSelectionCard,
  sortDestinationProducts,
} from './hotelSelectionStoreLogic';

export const useHotelSelectionStore = defineStore('HotelSelectionStore', () => {
  const recipeStore = useRecipeStore();
  const tripStore = useTripStore();
  const hotelSelectionModalsStore = useHotelSelectionModalsStore();
  const editingIngredientStore = useEditingIngredientStore();
  const itineraryStore = useItineraryStore();
  const globalCurrencyStore = useGlobalCurrencyStore();
  const hotelSelectionListStore = useHotelSelectionListStore();
  const statsigEventsStore = useStatsigEventsStore();
  const filtersStore = useFiltersStore();
  const routerStore = useRouterStore();
  const previouslyUserSessionStore = usePreviouslyUserSessionStore();
  const paxStore = usePaxStore();

  // State
  const viewType = ref<HotelSelectionViewType>(HotelSelectionViewType.List);
  const editingIngredientId = ref<IngredientId>(createIngredientId(''));
  const listSuggestionType = ref<SuggestionType>(SuggestionType.MostPopular);
  const editingStayIngredient = ref<HotelIngredient | OwnStayIngredient | null>(
    null
  );

  let polygonId: number | undefined;

  // Actions
  function initializeHotelSelectionView(stayOnlyIndex: number): void {
    const stayIngredient = recipeStore.stayOnlyRecipe[stayOnlyIndex] as
      | HotelIngredient
      | OwnStayIngredient;
    editingStayIngredient.value = stayIngredient;
    editingIngredientStore.setStayEditingIngredient(stayIngredient);
    editingIngredientId.value = stayIngredient.ingredientId;
    filtersStore.setHotelIngredientForFiltering();
    polygonId = tripStore.getMainPolygonId(editingIngredientId.value);
    hotelSelectionListStore.isFirstHotelSelectionListRender = true;

    if (isHotelIngredient(stayIngredient)) {
      listSuggestionType.value = mapAvailabilityOrderToSuggestionType(
        stayIngredient.availabilityOrder
      );
    }
    previouslyUserSessionStore.loadPreviouslySeenHotels();
  }

  function resetHotelSelectionView(): void {
    viewType.value = HotelSelectionViewType.List;
    listSuggestionType.value = SuggestionType.MostPopular;
    editingIngredientId.value = createIngredientId('');
    editingIngredientStore.doneEditingIngredient();
    hotelSelectionModalsStore.closeHotelSelectionModal();
  }

  function openHotelDetails(hotelCard: HotelSelectionCardData): void {
    routerStore.navigateToHotelDetailsInHotelSelection(
      { ingredientIndex: hotelCard.context.ingredientIndex },
      hotelCard.context.hotelCode
    );
  }

  function openFilters(): void {
    handleOpenFiltersEvent();
    hotelSelectionModalsStore.openFiltersModal();
  }

  function changeNumberOfNights(numberOfNights: number): void {
    const hotelIngredient = recipeStore.getHotelIngredient(
      editingIngredientId.value
    );
    hotelIngredient.numberOfNights = numberOfNights;
    trackNightsChanged();
  }

  function toggleMap(): void {
    viewType.value =
      viewType.value === HotelSelectionViewType.List
        ? HotelSelectionViewType.Map
        : HotelSelectionViewType.List;
  }

  function setListSuggestionType(suggestionType: SuggestionType): void {
    listSuggestionType.value = suggestionType;
  }

  function resetAllFilters(): void {
    editingIngredientStore.editingStayIngredient?.resetAllFilters();
    filtersStore.resetHotelFilters();

    handleEvents({
      type: HotelSelectionEventType.ResetFilter,
      data: {
        polygonId,
        label: editingIngredientStore.editingStayIngredient?.displayText ?? '',
      },
    });
  }

  function handleInitializedEvent(): void {
    handleEvents({
      type: HotelSelectionEventType.Initialized,
      data: {
        placeId: polygonId,
        placeName:
          editingIngredientStore.editingStayIngredient?.displayText ?? '',
        hotelResults: hotelSelectionCards.value.length,
      },
    });
  }

  function handleCloseFiltersEvent(
    origin: CloseHotelSelectionFiltersOrigin
  ): void {
    handleEvents({
      type: HotelSelectionEventType.CloseFilters,
      data: {
        polygonId,
        label: editingIngredientStore.editingStayIngredient?.displayText ?? '',
        hotelResults: hotelSelectionCards.value.length,
        eventPlacement: origin,
      },
    });
  }

  function handleOpenFiltersEvent(): void {
    handleEvents({
      type: HotelSelectionEventType.OpenFilters,
      data: {
        polygonId,
        label: editingIngredientStore.editingStayIngredient?.displayText ?? '',
        hotelResults: hotelSelectionCards.value.length,
      },
    });
  }

  function handleEvents(event: HotelSelectionEventTypes): void {
    if (event.type === HotelSelectionEventType.Initialized) {
      statsigEventsStore.logHotelSelectionInitiated(event.data);
    }

    if (event.type === HotelSelectionEventType.OpenFilters) {
      statsigEventsStore.logHotelSelectionFilterOpened(event.data);
    }

    if (event.type === HotelSelectionEventType.CloseFilters) {
      statsigEventsStore.logHotelSelectionFilterClosed(event.data);
    }

    if (event.type === HotelSelectionEventType.ApplyFilter) {
      statsigEventsStore.logHotelSelectionFilterApplied(event.data);
    }

    if (event.type === HotelSelectionEventType.RemoveFilter) {
      statsigEventsStore.logHotelSelectionFilterRemoved(event.data);
    }

    if (event.type === HotelSelectionEventType.ResetFilter) {
      statsigEventsStore.logHotelSelectionFilterReset(event.data);
    }
  }

  // Getters
  const hotelSelectionHeaderData = computed<
    HotelSelectionDestinationData | undefined
  >(() => {
    const ingredient = filtersStore.editedHotelIngredient;

    return getHotelSelectionHeaderData(
      editingIngredientStore.editingStayIngredient,
      ingredient
        ? itineraryStore.getHotelIngredientResponse(ingredient.ingredientId)
        : undefined,
      ingredient?.getActiveFiltersCount() ?? 0
    );
  });

  const hotelSelectionCards = computed<HotelSelectionCardData[]>(() => {
    let selectedHotelCode: string | undefined;

    if (editingIngredientStore.editingStayIngredient?.ingredientId) {
      const hotelFromItinerary = itineraryStore.getHotelIngredientResponse(
        editingIngredientStore.editingStayIngredient.ingredientId
      );

      if (
        hotelFromItinerary &&
        hotelFromItinerary.suggested &&
        hotelFromItinerary.suggested.code
      ) {
        selectedHotelCode = hotelFromItinerary.suggested.code;
      }
    }
    const availableProducts = sortDestinationProducts(
      tripStore.destinationProducts,
      listSuggestionType.value,
      selectedHotelCode
    );

    const previouslySeenHotels =
      previouslyUserSessionStore.previouslySeenHotelCodes;

    const recipeIngredientTypes = recipeStore.recipeWithoutOwnTransport.flatMap(
      (ingredient: RecipeMainIngredient) => {
        if (
          ingredient.ingredientType === IngredientType.Hotel ||
          ingredient.ingredientType === IngredientType.OwnStay
        ) {
          const result: IngredientType[] = [ingredient.ingredientType];
          const activitiesTotal = ingredient.subIngredients.length;
          Array.from({ length: activitiesTotal }).forEach(() =>
            result.push(IngredientType.Activity)
          );
          return result;
        }
        return [ingredient.ingredientType];
      }
    );

    return availableProducts.map((product) =>
      mapDestinationProductToHotelSelectionCard({
        product,
        formatMoney: globalCurrencyStore.formatMoney,
        getImageUrl,
        selectedHotelCode,
        ingredientIndex: recipeStore.getIngredientIndex(product.ingredientId),
        previouslySeenHotelCodes: previouslySeenHotels,
        isSuggestedHotel: tripStore.isSuggestedHotel(
          product.ingredientId,
          product.hotelCode
        ),
        recipeIngredientTypes,
        recipeWithoutOwnTransportIngredientIndex:
          recipeStore.recipeWithoutOwnTransport.findIndex(
            (recipeIngredinet) =>
              recipeIngredinet.ingredientId === product.ingredientId
          ),
        pax: paxStore.getPaxesButtonSimpleText,
      })
    );
  });

  const shouldDisplayLoader = computed<boolean>(
    () =>
      itineraryStore.isSearchingItineraries ||
      tripStore.isSearchingDestinationAvailability ||
      isFeatureToggleActive('loader')
  );

  const hotelMealFilterOptions = computed(
    () => filtersStore.getMealFitlerOptions
  );

  const hotelAmenitiesFilterOptions = computed(() =>
    filtersStore.getAmenitiesFitlerOptions()
  );

  const neighborhoodPolygonsFilterOptions = computed(
    () => filtersStore.getNeighborhoodPolygonsFilterOptions
  );

  const accommodationTypeFilterOptions = computed(() =>
    filtersStore.getAccommodationTypeFitlerOptions()
  );

  // Watchers
  const stopWatch = watch(shouldDisplayLoader, () => {
    if (shouldDisplayLoader.value) {
      const isInfiniteLoad = hotelSelectionCards.value.length > 0;

      if (isInfiniteLoad) {
        statsigEventsStore.logHotelSelectionInfiniteLoad();
      }

      stopWatch();
    }
  });

  return {
    // State
    viewType,
    listSuggestionType,
    editingIngredientId,
    editingStayIngredient,

    // Actions
    initializeHotelSelectionView,
    resetHotelSelectionView,
    openHotelDetails,
    openFilters,
    toggleMap,
    changeNumberOfNights,
    setListSuggestionType,
    resetAllFilters,
    handleInitializedEvent,
    handleCloseFiltersEvent,
    handleEvents,

    // Getters
    hotelSelectionHeaderData,
    hotelSelectionCards,
    shouldDisplayLoader,
    hotelMealFilterOptions,
    hotelAmenitiesFilterOptions,
    accommodationTypeFilterOptions,
    neighborhoodPolygonsFilterOptions,
  };
});
